import { useFlag } from '@calendly/feature-check-react'
import { isEmpty } from 'lodash-es'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { setGetParams } from 'actions/core/navigation'
import {
  clearStoredAiRailThreadId,
  openAiChatRailPane,
} from 'features/agent_hub/ai_chat_discovery/utils'
import { isAttachableRecapMeetingId } from 'features/agent_hub/utils/recapContext'
import { useRightRail } from 'features/right_rail'
import { useFetchPromptSuggestionsQuery } from 'features/tracked_meetings'

const parseAskIndex = (raw, questionsLength) => {
  const index = Number(raw)
  if (!Number.isInteger(index) || index < 1 || index > questionsLength) {
    return null
  }
  return index
}

/**
 * Re-arms the one-shot deep-link guard when the tracked meeting changes or when
 * inbound email markers (`askNotetaker`, `ask`, `askIndex`) reappear after URL cleanup.
 * Client-side nav between recaps keeps the parent hook mounted, so this must reset
 * `handledRef` without treating durable `pane=ai_chat` as a new deep link.
 *
 * @param {Object} options
 * @param {React.MutableRefObject<boolean>} options.handledRef
 * @param {string | undefined} options.meetingUuid
 * @param {string | null} options.askNotetaker
 * @param {string | null} options.askParam
 * @param {string | null} options.askIndexParam
 */
const useReArmRecapDeepLinkHandler = ({
  handledRef,
  meetingUuid,
  askNotetaker,
  askParam,
  askIndexParam,
}) => {
  const handledMeetingUuidRef = useRef(meetingUuid)
  const prevAskNotetakerRef = useRef(undefined)
  const prevAskParamRef = useRef(undefined)
  const prevAskIndexParamRef = useRef(undefined)

  useEffect(() => {
    if (handledMeetingUuidRef.current !== meetingUuid) {
      handledRef.current = false
      handledMeetingUuidRef.current = meetingUuid
    }

    const askNotetakerReappeared = (
      askNotetaker === 'True'
      && prevAskNotetakerRef.current !== 'True'
      && prevAskNotetakerRef.current !== undefined
    )
    const askReappeared = (
      askParam != null
      && askParam !== prevAskParamRef.current
      && prevAskParamRef.current !== undefined
    )
    const askIndexReappeared = (
      askIndexParam != null
      && askIndexParam !== prevAskIndexParamRef.current
      && prevAskIndexParamRef.current !== undefined
    )
    if (askNotetakerReappeared || askReappeared || askIndexReappeared) {
      handledRef.current = false
    }
    prevAskNotetakerRef.current = askNotetaker
    prevAskParamRef.current = askParam
    prevAskIndexParamRef.current = askIndexParam
  }, [askIndexParam, askNotetaker, askParam, handledRef, meetingUuid])
}

/**
 * Opens a fresh Callie rail for one-shot recap email deep links once the meeting
 * is loaded and, when `ask` / `askIndex` require it, suggestions have settled
 * (RTK success or error — not merely `!isLoading`, which can be false in the
 * skip→fetch gap with no data yet).
 *
 * Clears inbound markers after open; passes `pendingAskIndex` via pane props only.
 *
 * @param {Object} options
 * @param {React.MutableRefObject<boolean>} options.handledRef
 * @param {boolean} options.isRecapIntegrationEnabled
 * @param {boolean} options.isMeetingLoaded
 * @param {boolean} options.isDeepLink
 * @param {boolean} options.isSuggestionsPending
 * @param {boolean} options.isSuggestionsError
 * @param {Object | undefined} options.promptSuggestions
 * @param {string | null} options.askIndexParam
 * @param {string | null} options.askParam
 * @param {boolean} options.canAttach
 * @param {string | undefined} options.meetingUuid
 * @param {string | undefined} options.meetingTitle
 * @param {(name: string, props?: Object) => void} options.openOrReplace
 */
const useOpenRecapChatFromDeepLink = ({
  handledRef,
  isRecapIntegrationEnabled,
  isMeetingLoaded,
  isDeepLink,
  isSuggestionsPending,
  isSuggestionsError,
  promptSuggestions,
  askIndexParam,
  askParam,
  canAttach,
  meetingUuid,
  meetingTitle,
  openOrReplace,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    if (!isRecapIntegrationEnabled || !isMeetingLoaded || !isDeepLink || handledRef.current) {
      return
    }

    if (isSuggestionsPending) {
      return
    }

    handledRef.current = true

    clearStoredAiRailThreadId()
    const questions = (!isSuggestionsError && promptSuggestions?.questions) || []
    let pendingAskIndex

    if (askIndexParam != null) {
      pendingAskIndex = parseAskIndex(askIndexParam, questions.length) ?? undefined
    } else if (askParam != null) {
      const matchIndex = questions.findIndex((entry) => entry?.question === askParam)
      if (matchIndex >= 0) {
        pendingAskIndex = matchIndex + 1
      }
    }

    // Bad ask / out-of-range askIndex: open compose-only (no pendingAskIndex).
    // Future: surface a user-facing warning here if product wants one.

    const paneProps = {
      forceNewChat: true,
      entrySource: 'recap_email',
      ...(canAttach ? {
        attachRecapContext: true,
        trackedMeetingUuid: meetingUuid,
        ...(meetingTitle ? { trackedMeetingTitle: meetingTitle } : {}),
      } : {}),
      ...(pendingAskIndex != null ? { pendingAskIndex } : {}),
    }

    openAiChatRailPane(openOrReplace, paneProps)

    // Clear one-shot markers. Pass pendingAskIndex via pane props only — do not leave
    // askIndex in the URL (refresh / remount would re-fire). Pane is set by openAiChatRailPane.
    // Preserve source=email until useRecapEmailCTACheck classifies the CTA and clears it.
    dispatch(setGetParams({
      ask: null,
      askNotetaker: null,
      askIndex: null,
      pane: 'ai_chat',
    }))
  }, [
    askIndexParam,
    askParam,
    canAttach,
    dispatch,
    handledRef,
    isDeepLink,
    isMeetingLoaded,
    isRecapIntegrationEnabled,
    isSuggestionsError,
    isSuggestionsPending,
    meetingTitle,
    meetingUuid,
    openOrReplace,
    promptSuggestions?.questions,
  ])
}

/**
 * Email "Ask Callie" links may open the rail with durable `pane=ai_chat` only (no one-shot
 * markers). Stamp `entrySource` so composer analytics use RECAP_EMAIL — without forceNewChat.
 * Skip when a deep link already opened (handledRef) — that path sets entrySource itself.
 *
 * @param {Object} options
 * @param {React.MutableRefObject<boolean>} options.handledRef
 * @param {boolean} options.isRecapIntegrationEnabled
 * @param {boolean} options.isMeetingLoaded
 * @param {boolean} options.isDeepLink
 * @param {boolean} options.canAttach
 * @param {string | undefined} options.meetingUuid
 * @param {string | undefined} options.meetingTitle
 * @param {string} options.locationSearch
 * @param {Object | null | undefined} options.currentPaneProps
 * @param {(name: string, props?: Object) => void} options.openOrReplace
 */
const useStampRecapEmailEntrySource = ({
  handledRef,
  isRecapIntegrationEnabled,
  isMeetingLoaded,
  isDeepLink,
  canAttach,
  meetingUuid,
  meetingTitle,
  locationSearch,
  currentPaneProps,
  openOrReplace,
}) => {
  const stampedRef = useRef(false)

  useEffect(() => {
    if (handledRef.current || currentPaneProps?.entrySource === 'recap_email') {
      stampedRef.current = true
      return
    }

    if (
      !isRecapIntegrationEnabled
      || !isMeetingLoaded
      || isDeepLink
      || stampedRef.current
    ) {
      return
    }

    const params = new URLSearchParams(locationSearch)
    if (params.get('source') !== 'email' || params.get('pane') !== 'ai_chat') {
      return
    }

    stampedRef.current = true
    openAiChatRailPane(openOrReplace, {
      ...(currentPaneProps ?? {}),
      entrySource: 'recap_email',
      ...(canAttach ? {
        attachRecapContext: true,
        trackedMeetingUuid: meetingUuid,
        ...(meetingTitle ? { trackedMeetingTitle: meetingTitle } : {}),
      } : {}),
    })
  }, [
    canAttach,
    currentPaneProps,
    handledRef,
    isDeepLink,
    isMeetingLoaded,
    isRecapIntegrationEnabled,
    locationSearch,
    meetingTitle,
    meetingUuid,
    openOrReplace,
  ])
}

/**
 * Flag-on routing for recap email / Ask Callie deep links into a fresh ai_chat rail.
 * Mount from tracked meeting details once meeting data is loaded.
 *
 * Inbound markers are one-shot query params (`askNotetaker`, `ask`, `askIndex`) — never
 * durable right-rail state like bare `pane=ai_chat`, which stays in the URL while Callie
 * is open and would otherwise retrigger `forceNewChat` on remount / recap nav.
 *
 * @param {Object} options
 * @param {string | undefined} options.meetingUuid — prefer details payload `data.uuid`
 * @param {string | undefined} [options.meetingTitle]
 * @param {boolean} options.isMeetingLoaded
 */
export const useRecapChatDeepLink = ({
  meetingUuid,
  meetingTitle,
  isMeetingLoaded = false,
}) => {
  const isRecapIntegrationEnabled = useFlag('ask_callie_recaps_page_integration')
  const location = useLocation()
  const { openOrReplace, current } = useRightRail()
  /** One-shot per deep-link request; re-armed when meeting or inbound query markers change. */
  const handledRef = useRef(false)

  const params = new URLSearchParams(location.search)
  const askNotetaker = params.get('askNotetaker')
  // `URLSearchParams.get` returns '' for bare `?ask=` / `?askIndex=` — treat as absent.
  // Reject non-positive / non-integer askIndex the same way open-path parseAskIndex does.
  const rawAskIndex = params.get('askIndex')
  const askIndexParam = (() => {
    if (isEmpty(rawAskIndex)) return null
    const index = Number(rawAskIndex)
    if (!Number.isInteger(index) || index < 1) return null
    return rawAskIndex
  })()
  const rawAsk = params.get('ask')
  const askParam = isEmpty(rawAsk) ? null : rawAsk

  // One-shot inbound markers only — not `pane=ai_chat` (normal Callie rail URL state).
  const isDeepLink = Boolean(
    askNotetaker === 'True'
    || askIndexParam != null
    || askParam != null,
  )

  const canAttach = isAttachableRecapMeetingId(meetingUuid)
  const needsSuggestions = isDeepLink
    && isRecapIntegrationEnabled
    && isMeetingLoaded
    && (askIndexParam != null || askParam != null)
    && canAttach

  const {
    data: promptSuggestions,
    isError: isSuggestionsError,
    isSuccess: isSuggestionsSuccess,
  } = useFetchPromptSuggestionsQuery(
    { meetingUuid },
    { skip: !needsSuggestions },
  )

  // After skip→fetch, RTK can briefly report isLoading:false with no data. Wait until the
  // query settles (success or error) before consuming askIndex/ask so we do not drop them.
  const isSuggestionsPending = needsSuggestions
    && !isSuggestionsSuccess
    && !isSuggestionsError

  useReArmRecapDeepLinkHandler({
    handledRef,
    meetingUuid,
    askNotetaker,
    askParam,
    askIndexParam,
  })

  useOpenRecapChatFromDeepLink({
    handledRef,
    isRecapIntegrationEnabled,
    isMeetingLoaded,
    isDeepLink,
    isSuggestionsPending,
    isSuggestionsError,
    promptSuggestions,
    askIndexParam,
    askParam,
    canAttach,
    meetingUuid,
    meetingTitle,
    openOrReplace,
  })

  useStampRecapEmailEntrySource({
    handledRef,
    isRecapIntegrationEnabled,
    isMeetingLoaded,
    isDeepLink,
    canAttach,
    meetingUuid,
    meetingTitle,
    locationSearch: location.search,
    currentPaneProps: current?.paneProps,
    openOrReplace,
  })
}
